import { getRuntimeConfig } from "@/shared/config"

import { readFeatureFlagOverrides } from "./featureFlagOverrides"

/**
 * Compile-time feature flag sets — edit values here and redeploy.
 *
 * - **NON_PROD** — `npm run dev`, `npm run build:dev`, staging deploy (`config.env !== "prod"`).
 * - **PROD** — production deploy (`config.env === "prod"`).
 *
 * Per-device overrides (Profile → dev menu) are stored in localStorage and merged on each
 * `isFeatureEnabled` read; toggles take effect after reopening the mini app.
 */
export const FEATURE_FLAGS_NON_PROD = {
  /** Banner, welcome modal, Rewards card, leaderboard, bootstrap + WS feed. */
  tournaments: true,
  /** Inventory tab, gift bets/deposits/withdrawals, marketplace, gift WS feed. */
  gifts: true,
} as const satisfies Record<string, boolean>

export const FEATURE_FLAGS_PROD = {
  tournaments: false,
  gifts: true,
} as const satisfies Record<string, boolean>

export type FeatureFlag = keyof typeof FEATURE_FLAGS_NON_PROD

type FeatureFlagSet = Record<FeatureFlag, boolean>

/** Build-time defaults for the active environment (no localStorage overrides). */
export function getCompileTimeFeatureFlags(): FeatureFlagSet {
  if (import.meta.env.DEV) {
    return { ...FEATURE_FLAGS_NON_PROD }
  }

  try {
    return getRuntimeConfig().env === "prod"
      ? { ...FEATURE_FLAGS_PROD }
      : { ...FEATURE_FLAGS_NON_PROD }
  } catch {
    return { ...FEATURE_FLAGS_PROD }
  }
}

function mergeFeatureFlagOverrides(base: FeatureFlagSet): FeatureFlagSet {
  const overrides = readFeatureFlagOverrides()
  return {
    tournaments: overrides.tournaments ?? base.tournaments,
    gifts: overrides.gifts ?? base.gifts,
  }
}

function resolveFeatureFlags(): FeatureFlagSet {
  return mergeFeatureFlagOverrides(getCompileTimeFeatureFlags())
}

export function isFeatureEnabled(flag: FeatureFlag): boolean {
  return resolveFeatureFlags()[flag]
}

/** React hook wrapper — resolves against the active flag set. */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  return isFeatureEnabled(flag)
}
